import { getConfig, getErrorMessage, klinikenApi } from "@/api";
import store, { useStore } from "@/store";
import { SparrValue } from "@/store/modules/recept/helpers";
import { StoredReceptforskrivning } from "@/store/modules/recept/receptforskrivningModule";
import {
    ArtikelInfo,
    Handelsvara,
    HandelsvaraDisplay,
    ReceptforskrivningDisplay,
    Sparr,
} from "@/tabs/Receptforskrivning/types";
import { openDialog } from "@/utils";
import { AxiosError } from "axios";
import moment from "moment/moment";

export enum SilProductType {
    Approved = "APPROVED",
    NonAproved = "NON_APPROVED",
}

// Duplicerad i module, pga att Vue inte klarar av att hantera enum imports på ett vettigt sätt
enum ValidationSeverity {
    Low = "low",
    Warning = "warning",
    Moderate = "moderate",
    Error = "error",
}

export type NLLIssue = { code: string; display: string; severity: ValidationSeverity };

export const ANNAT_VAL = "OTH";

export const NLL_GENERAL_ERROR_DESCRIPTION =
    "Det kan bero på ett tekniskt fel i NLL eller Kliniken. Du kan försöka igen senare. Om felet kvarstår, kontakta supporten.";

export const openNLLErrorDialog = ({ error, description }: { error: AxiosError; description?: string }): void => {
    const options: { type: string; description?: string; lines?: string[] } = {
        type: "error",
    };
    if (description) {
        options.description = description;
        options.lines = getNLLOrInternalErrorMessage(error)?.split("\n");
    } else {
        options.description = getNLLOrInternalErrorMessage(error);
    }
    openDialog(options);
};

/**
 * Plockar ut felmeddelanden som är relevanta för användaren.
 * @param error AxiosError-objekt där response.data kan innehålla antingen NLL-fel eller Kliniken-fel
 * @returns En sträng av läsbara felmeddelanden antingen från NLL eller Kliniken
 */
const getNLLOrInternalErrorMessage = (error: AxiosError): string | undefined => {
    let data = error?.response?.data;

    const nllIssueAttrs = ["code", "display", "severity"];

    // Om datan har formen av NLL-felmeddelanden, antingen som ett enskilt objekt eller en lista av objekt:
    if (
        (typeof data === "object" && nllIssueAttrs.every((attr) => attr in data)) ||
        (Array.isArray(data) && nllIssueAttrs.every((attr) => attr in data?.[0]))
    ) {
        const humanReadebleErrors = filterNLLIssuesHumanReadable(filterNLLIssuesSevere(data));

        if (humanReadebleErrors?.length) {
            return humanReadebleErrors.map((message) => message.display).join("\n");
        } else {
            return NLL_GENERAL_ERROR_DESCRIPTION;
        }
    }

    // Fallback: Klinikens egen felhantering
    return getErrorMessage(error);
};

/**
 * Filtrerar NLL-issues till enbart de som blockerar en handling (t.ex. skicka förskrivning).
 **/
export const filterNLLIssuesSevere = (issues?: Array<NLLIssue>): Array<NLLIssue> | undefined => {
    if (!Array.isArray(issues)) {
        issues = issues ? [issues] : [];
    }

    // Moderate och Error blockerar skickning av förskrivning.
    return issues?.filter((message) =>
        [ValidationSeverity.Error, ValidationSeverity.Moderate].includes(message.severity)
    );
};

/**
 * Filtrerar NLL-issues till enbart de som är läsliga för användaren.
 **/
export const filterNLLIssuesHumanReadable = (issues?: Array<NLLIssue>): Array<NLLIssue> | undefined => {
    if (!Array.isArray(issues)) {
        issues = issues ? [issues] : [];
    }

    // Felmeddelanden som är till användaren skickas med code som börjar med bokstav och punkt (t.ex G.55).
    // Code som börjar med siffra är inte human-readable.
    return issues?.filter((message) => message?.code.match(/^[a-zA-Z]\./));
};

export const parseMedicationRequestErrors = ({
    data,
    id,
    save,
}: {
    data?: StoredReceptforskrivning["errorMessages"];
    id: StoredReceptforskrivning["id"];
    /**
     * Ska errors sparas i receptförskrivningen i store. Används för samlingar
     */
    save?: boolean;
}) => {
    const severeIssues = filterNLLIssuesSevere(data);

    if (severeIssues?.length) {
        if (save) {
            store.commit("receptforskrivning/partialUpdateReceptforskrivning", {
                id,
                receptforskrivning: {
                    errorMessages: severeIssues,
                },
            });
        }

        return filterNLLIssuesHumanReadable(severeIssues)?.map((message) => message.display);
    }

    return [];
};

/**
 * Stor bokstav i början på inskickad text
 */
export const capitalize = ({
    text,
}: {
    /**
     * Sträng som ska manipuleras
     */
    text?: string | null;
}) => {
    if (!text) {
        return null;
    }

    return `${text.slice(0, 1).toUpperCase()}${text.slice(1) ? text.slice(1) : ""}`;
};

export const joinWithOch = (list: string[]): string => {
    if (list.length === 0) return "";
    if (list.length === 1) return list[0];
    if (list.length === 2) return list[0] + " och " + list[1];
    return list.slice(0, -1).join(", ") + " och " + list[list.length - 1];
};

export const convertHandelsvaraData: (args: { handelsvara?: Handelsvara }) => HandelsvaraDisplay | null = ({
    handelsvara,
}) => {
    if (!handelsvara) {
        return null;
    }

    return {
        id: handelsvara.articleNo,
        styrka: handelsvara.strengthText,
        benamning: handelsvara.productDescription,
        lakemedelInfo: {
            namn: handelsvara.articleName,
            tillverkare: handelsvara.companySupplierName,
            varunr: handelsvara.articleNo,
            benamning: handelsvara.productDescription,
            storlek: handelsvara.sizeText,
        },
        formansrestriktion: handelsvara.limitedReimbursement,
        inomForman: handelsvara.isReimbursed,
        forpackning: handelsvara.packDescription,
        mangd: handelsvara.sizeNum,
    };
};

const sendForskrivning = async ({
    receptforskrivning,
    headers,
    saveErrors,
    fornya,
}: {
    receptforskrivning: StoredReceptforskrivning;
    headers: { [key: string]: string };
    /**
     * Ska errors sparas i receptförskrivningen i store. Används för samlingar
     */
    saveErrors?: boolean;
    fornya?: boolean;
}) => {
    try {
        const {
            lakemedelId,
            nplId,
            // START Ska inte skickas till BE. Duplicerat i ReceptforskrivningForm
            id,
            validationMessages,
            sokLakemedel,
            silProductType,
            forhandsvisning,
            visa_administrering,
            administrering_all,
            mallAutocomplete,
            // apotek ska skickas in som string till BE
            apotek,
            // END Ska inte skickas till BE
            ...payload
        } = receptforskrivning;

        store.commit("receptforskrivning/updateStatus", { id, status: "Loading" });

        const { data } = await klinikenApi.post<
            | Array<{
                  code: string;
                  display: string;
                  severity: ValidationSeverity;
              }>
            | Record<string, unknown>
        >(
            fornya ? "nll/forskrivningar/fornya/" : "nll/forskrivningar/create/",
            {
                ...payload,
                apotek: apotek?.value,
                patient: store.getters["patientData/currentId"],
                lakemedel_npl_id: nplId,
                lakemedel_npl_pack_id: lakemedelId,
            },
            getConfig({
                headers,
            })
        );

        if (!Array.isArray(data)) {
            store.commit("receptforskrivning/updateStatus", { id, status: "Success" });

            return;
        }

        const humanErrorMessages = parseMedicationRequestErrors({ id, data, save: saveErrors });

        const updatedForskrivning = store.getters["receptforskrivning/receptforskrivningById"]({ id });

        // Om det finns fel öht (även human non-readable) - kasta error, men visa bara de läsbara
        if (updatedForskrivning.errorMessages.length) {
            if (humanErrorMessages?.length) {
                throw new Error(humanErrorMessages.join("\n"));
            } else {
                throw new Error(NLL_GENERAL_ERROR_DESCRIPTION);
            }
        }
    } catch (error) {
        console.error(error);

        if ((error as AxiosError)?.response?.data?.length) {
            console.error((error as AxiosError)?.response?.data);
        }

        store.commit("receptforskrivning/updateStatus", { id: receptforskrivning.id, status: "Error" });

        const humanErrorMessages = parseMedicationRequestErrors({
            id: receptforskrivning.id,
            data: (error as AxiosError)?.response?.data,
            save: saveErrors,
        });

        if (humanErrorMessages?.length) {
            (error as AxiosError).message = humanErrorMessages.join("\n");
        } else {
            (error as Error).message = NLL_GENERAL_ERROR_DESCRIPTION;
        }

        throw error;
    }
};

export const sendForskrivningar = async ({
    receptforskrivningar,
    headers,
    saveErrors,
    fornya,
}: {
    receptforskrivningar: StoredReceptforskrivning[];
    headers: { [key: string]: string };
    /**
     * Ska errors sparas i receptförskrivningen i store. Används för samlingar
     */
    saveErrors?: boolean;
    fornya?: boolean;
}) => {
    const store = useStore();

    if (receptforskrivningar.length === 1) {
        await sendForskrivning({ headers, saveErrors, fornya, receptforskrivning: receptforskrivningar[0] });
        await store.dispatch("nll/fetchPatientinfo");

        return;
    }

    const result = [];

    for await (const receptforskrivning of receptforskrivningar) {
        try {
            await sendForskrivning({ receptforskrivning, headers, saveErrors, fornya });

            result.push({ status: "fulfilled" });
        } catch (error) {
            result.push({ status: "rejected" });
        }
    }

    if (result.find((singleResult) => singleResult.status === "fulfilled")) {
        await store.dispatch("nll/fetchPatientinfo");
    }

    return result;
};

export const sendSparr = async ({
    patient_id,
    forskrivningskedja_id,
    sparr,
    headers,
}: {
    patient_id: string;
    forskrivningskedja_id?: string;
    sparr: SparrValue;
    headers: { [key: string]: string };
}) => {
    try {
        await klinikenApi.post(
            "nll/sparrar/create/",
            {
                patient_id: patient_id,
                prescription_chain_id: forskrivningskedja_id,
                legal_ground: sparr.sparr_legal_ground,
                subject_type: sparr.sparr_subject_type,
            },
            getConfig({
                headers,
            })
        );
    } catch (error: any) {
        console.error(error);

        openNLLErrorDialog({ error, description: "Det gick inte att skicka spärren." });

        throw error;
    }
};

export const inactivateSparr = async ({
    patient_id,
    sparr_id,
    headers,
}: {
    patient_id: string;
    sparr_id: string;
    headers: { [key: string]: string };
}) => {
    try {
        await klinikenApi.post(
            `nll/sparrar/${patient_id}/inactivate/${sparr_id}/`,
            {},
            getConfig({
                headers,
            })
        );
    } catch (error: any) {
        console.error(error);

        openNLLErrorDialog({ error, description: "Det gick inte att häva spärren." });

        throw error;
    }
};

export type NllLocksDisplay = {
    integritet: boolean;
    behandlingsorsak: boolean;
    sekretessVardnadshavare: boolean;
    behandlingsorsakIntegritet: boolean;
};

export const checkNllLocks = ({ sparrar }: { sparrar?: ReceptforskrivningDisplay["sparrar"] }) => {
    if (!sparrar) {
        return null;
    }

    const locks: NllLocksDisplay = {
        integritet: false,
        behandlingsorsak: false,
        sekretessVardnadshavare: false,
        behandlingsorsakIntegritet: false,
    };

    sparrar.forEach((sparr) => {
        if (sparr.status !== "active") {
            return;
        }

        if (sparr.legal_ground === "INTEGRITET" && sparr.subject_type === "BEHANDLINGSORSAK") {
            locks.behandlingsorsakIntegritet = true;
        } else if (sparr.legal_ground === "INTEGRITET") {
            locks.integritet = true;
        } else if (sparr.legal_ground === "SEKRETESS_VH") {
            locks.sekretessVardnadshavare = true;
        } else if (sparr.subject_type === "BEHANDLINGSORSAK") {
            locks.behandlingsorsak = true;
        }
    });

    return locks;
};

export const findNllLockBehandlingsorsak = ({
    sparrar,
}: {
    sparrar: ReceptforskrivningDisplay["sparrar"];
}): Sparr | undefined => {
    return sparrar?.find(
        (sparr) =>
            sparr.subject_type === "BEHANDLINGSORSAK" &&
            sparr.legal_ground !== "INTEGRITET" &&
            sparr.status === "active"
    );
};

export const findNllLockSekretessVardnadshavare = ({
    sparrar,
}: {
    sparrar: ReceptforskrivningDisplay["sparrar"];
}): Sparr | undefined => {
    return sparrar?.find((sparr) => sparr.legal_ground === "SEKRETESS_VH" && sparr.status === "active");
};

export const hasPrescriptionRight = (profile: any) => {
    return !!profile.personalPrescriptionCode;
};

export const hasNLLReadPermission = (profile: any) => {
    return ["SJ", "DT"].includes(profile.yrkeskategori.code) || !!hasPrescriptionRight(profile);
};

export const formatProductTitle = ({
    artikelInfo,
    isUttag = false,
}: {
    artikelInfo: ArtikelInfo;
    isUttag?: boolean;
}) => {
    if ("varunr" in artikelInfo) {
        const parts = [artikelInfo.benamning, artikelInfo.storlek, artikelInfo.tillverkare, artikelInfo.varunr].filter(
            Boolean
        );

        return parts.join(", ");
    }

    let parts = [
        artikelInfo.namn,
        [artikelInfo.styrka, artikelInfo.form].filter(Boolean).join(" "),
        artikelInfo.tillverkare,
    ];

    // Bara vid uttag behöver vi visa dessa, då den ordinarie visar dem i tabellen
    if (isUttag) {
        parts = parts.concat([artikelInfo.forpackningsinnehall, artikelInfo.substans, artikelInfo.atc]);
    }

    return parts.filter(Boolean).join(", ");
};

export const getDefaultFormValues = ({ values }: { values?: { [key: string]: any } } = {}) => {
    const store = useStore();
    const patient = store.getters["patientData/patient"];

    return {
        formansberattigad_patient: !!patient.careCoverage,
        visa_administrering: ["metod"],
        administreringsmetod: null,
        silProductType: SilProductType.Approved,
        substitution_tillaten: true,
        sista_giltighetsdag: moment().add(1, "year").format("yyyy-MM-DD HH:mm"),
        forskrivning_enligt_smittskyddslagen: false,
        // Placerad på botten för att ersätta värden ovan vid behov (t.ex sätts silProductType till nuvarande värde
        // i formuläret vid vissa rensningar av formuläret
        ...values,
    };
};
